import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, FileText, Tag, Eye, Upload, Trash2 } from 'lucide-react';
import { api, uploadFile } from '@/api/client';
import { SignedImage } from '@/components/ui/SignedImage';

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const LOGO_MAX_BYTES = 500 * 1024;

interface Merchant {
  id: number;
  companyName: string;
  logoUrl?: string | null;
  labelSize?: string;
  labelTemplate?: string;
  labelHeader?: string;
  labelFooter?: string;
  labelShowProduct?: boolean;
  labelShowFromMobile?: boolean;
  labelShowTopBanner?: boolean;
  labelShowFrom?: boolean;
  labelShowLogo?: boolean;
}

const SIZES = [
  { id: '4x6', label: '4 × 6 in', sub: '100 × 150 mm — industry default', mm: '100×150' },
  { id: '6x3', label: '6 × 3 in', sub: '150 × 75 mm — wide', mm: '150×75' },
  { id: '4x4', label: '4 × 4 in', sub: '100 × 100 mm — square', mm: '100×100' },
];

const TEMPLATES = [
  { id: 'STANDARD', name: 'Standard', sub: 'Simple to-from layout — current default', preview: '<div style="font-family:Arial;font-size:9px;padding:4px;border:1px solid #94a3b8"><div style="display:flex;justify-content:space-between;border-bottom:1px solid #000;padding-bottom:2px;font-weight:700"><span>BLUEDART</span><span>COD</span></div><div style="font-family:monospace;font-weight:700">GNT0010000003</div><div style="display:flex;gap:6px;margin-top:2px"><div style="flex:1"><b>John Doe</b><br>5 MG Road<br>Mumbai 400070</div><div style="border-left:1px dashed #000;padding-left:4px;width:35%"><b>From:</b><br>Acme Ltd<br>Noida 201301</div></div><div style="border-top:1px solid #000;margin-top:2px;padding-top:2px;display:flex;justify-content:space-between;font-weight:700"><span>B2C-001</span><span>0.5 kg</span><span>COD ₹999</span></div></div>' },
  { id: 'INVOICE_STYLE', name: 'Invoice Style', sub: 'Bluedart-like — routing code, terms, RTO block, order barcode', preview: '<div style="border:2px solid #000;padding:4px;font-size:9px;font-family:Arial"><div style="display:flex;justify-content:space-between;border-bottom:1px solid #000;padding-bottom:2px"><div><b>Ship To</b><br>Customer<br>City<br>PIN xxxxxx</div><div style="width:48%;text-align:center"><b>Courier: Bluedart</b><br><span style="font-family:monospace;font-size:12pt">||||| |||</span><br>Awb: 4703XXXX</div></div><div style="border-bottom:1px solid #000;padding:2px 0"><b>Dim:</b> 15×19×9 · <b>Wt:</b> 0.3KG · <b>COD:</b> ₹3550</div><div style="border-bottom:1px solid #000;padding:2px 0"><b>Shipped By</b> <span>(if undelivered)</span><br>Company Name · City</div><div style="padding:2px 0;font-size:7px"><b>T&C:</b> 1. Visit courier site. 2. All figures in INR.</div></div>' },
  { id: 'ECOM_STYLE', name: 'E-com Style (QR + Barcode)', sub: 'Delhivery-like — QR code, customer table, RTO bottom', preview: '<div style="border:2px dashed #000;padding:6px;font-size:9px;font-family:Arial"><div style="text-align:center"><b>Ship By - Delhivery</b><br><b>Order No - ORD10245</b><br>Wt 4.0 · Dim 26×22×17<br><b>MODE - PREPAID</b></div><div style="display:flex;align-items:center;justify-content:space-between;gap:4px;margin-top:3px"><div style="flex:1;font-family:monospace;font-size:14pt;text-align:center">|| |||| | |||</div><div style="width:40px;height:40px;border:1px solid #000;background:#f1f5f9;display:flex;align-items:center;justify-content:center;font-size:8pt;font-weight:700">QR</div></div><table style="width:100%;border-collapse:collapse;margin-top:2px"><tr><td style="border:1px solid #000;padding:2px"><b>Customer</b></td><td style="border:1px solid #000;padding:2px">SAJID KHAN<br>Bhopal 464224</td></tr></table><div style="text-align:center;font-weight:700;border:1px solid #000;margin-top:2px;padding:2px">Total: ₹10000.00</div><div style="margin-top:2px"><b>RTO -</b> Khasra No. 524, Noida</div></div>' },
];

export function LabelSettingsPanel({ merchantId, embedded }: { merchantId?: number; embedded?: boolean }) {
  const qc = useQueryClient();
  // Admin context passes a merchantId and uses the admin routes; the merchant portal passes none and
  // uses its own-account routes. `/merchants/me` (no suffix) does NOT exist — `@Get(':id')` would
  // swallow it and fail on ParseIntPipe — so the merchant branch must be `/merchants/me/profile`.
  const url = merchantId ? `/merchants/${merchantId}` : '/merchants/me/profile';

  const { data, isLoading } = useQuery({
    queryKey: ['merchant-label-prefs', merchantId ?? 'me'],
    queryFn: async () => (await api.get<Merchant>(url)).data,
  });

  const [size, setSize] = useState('4x6');
  const [tpl, setTpl] = useState('STANDARD');
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [showProduct, setShowProduct] = useState(false);
  const [showFromMobile, setShowFromMobile] = useState(false);
  const [showTopBanner, setShowTopBanner] = useState(true);
  const [showFrom, setShowFrom] = useState(true);
  const [showLogo, setShowLogo] = useState(true);

  useEffect(() => {
    if (data) {
      setSize(data.labelSize ?? '4x6');
      setTpl(data.labelTemplate ?? 'STANDARD');
      setHeader(data.labelHeader ?? '');
      setFooter(data.labelFooter ?? '');
      setShowProduct(!!data.labelShowProduct);
      setShowFromMobile(!!data.labelShowFromMobile);
      setShowTopBanner(data.labelShowTopBanner !== false);
      setShowFrom(data.labelShowFrom !== false);
      setShowLogo(data.labelShowLogo !== false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.patch(url, { labelSize: size, labelTemplate: tpl, labelHeader: header || null, labelFooter: footer || null, labelShowProduct: showProduct, labelShowFromMobile: showFromMobile, labelShowTopBanner: showTopBanner, labelShowFrom: showFrom, labelShowLogo: showLogo })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-label-prefs'] }); toast.success('Label settings saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  // The logo lives on the merchant profile, but it's the thing being printed HERE — so it can be
  // uploaded/removed in this panel too, and saves immediately (no "Save Settings" needed) so the live
  // preview below can show the real label straight away.
  const fileRef = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const saveLogo = async (ref: string | null) => {
    setLogoBusy(true);
    try {
      await api.patch(url, { logoUrl: ref });
      await qc.invalidateQueries({ queryKey: ['merchant-label-prefs'] });
      await qc.invalidateQueries({ queryKey: ['label-preview'] });
      toast.success(ref ? 'Logo uploaded' : 'Logo removed');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not save the logo');
    } finally { setLogoBusy(false); }
  };
  const pickLogo = async (file: File) => {
    if (!LOGO_TYPES.includes(file.type)) { toast.error('Only PNG and JPG/JPEG are allowed.'); return; }
    if (file.size > LOGO_MAX_BYTES) { toast.error(`File too large (${Math.round(file.size / 1024)} KB). Max 500 KB.`); return; }
    setLogoBusy(true);
    try {
      const { ref } = await uploadFile(file, 'merchant_logo');
      await saveLogo(ref);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed');
    } finally { setLogoBusy(false); }
  };

  // Live preview HTML — fetched via axios (so JWT goes via api client) then injected into an iframe srcDoc
  const previewMerchantId = merchantId ?? data?.id;
  const { data: previewHtml, isFetching: previewLoading } = useQuery({
    queryKey: ['label-preview', previewMerchantId, size, tpl, showProduct, showFromMobile, showTopBanner, showFrom, showLogo],
    enabled: !!previewMerchantId,
    queryFn: async () => (await api.get<string>(`/orders/labels/preview`, {
      params: { merchantId: previewMerchantId, size, template: tpl, showProduct, showFromMobile, showTopBanner, showFrom, showLogo },
      transformResponse: (r: any) => r,
      responseType: 'text',
    })).data,
    staleTime: 5_000,
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className={embedded ? '' : 'p-6 max-w-[1000px] mx-auto'}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Tag size={18} /> Label Settings</h2>
        <p className="text-sm text-slate-500">Choose size, template and any header/footer text printed on every shipping label.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        {/* Size */}
        <div>
          <div className="text-xs uppercase font-medium tracking-wide text-slate-500 mb-2">Label Size *</div>
          <div className="grid grid-cols-3 gap-2">
            {SIZES.map(s => (
              <button key={s.id} onClick={() => setSize(s.id)}
                className={`text-left p-3 border rounded-lg transition ${size === s.id ? 'border-primary bg-primary-50 ring-2 ring-primary/20' : 'border-slate-200 hover:bg-slate-50'}`}>
                <div className="font-semibold text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Template */}
        <div>
          <div className="text-xs uppercase font-medium tracking-wide text-slate-500 mb-2">Template *</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TEMPLATES.map(t => (
              <button key={t.id} onClick={() => setTpl(t.id)}
                className={`text-left p-3 border rounded-lg transition ${tpl === t.id ? 'border-primary bg-primary-50 ring-2 ring-primary/20' : 'border-slate-200 hover:bg-slate-50'}`}>
                <div className="font-semibold text-slate-900 flex items-center gap-1.5"><FileText size={14} /> {t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 mb-2">{t.sub}</div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2 overflow-hidden" style={{ minHeight: 110 }} dangerouslySetInnerHTML={{ __html: t.preview }} />
              </button>
            ))}
          </div>
        </div>

        {/* Live preview — actual size barcode + QR rendered against synthetic test data */}
        <div>
          <div className="text-xs uppercase font-medium tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
            <Eye size={12} /> Live preview ({size} · {tpl})
            {previewLoading && <Loader2 size={12} className="animate-spin text-slate-400" />}
          </div>
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 flex justify-center">
            <iframe
              key={`${size}-${tpl}-${showProduct}-${showLogo}`}
              title="Label preview"
              srcDoc={previewHtml ?? '<div style="padding:40px;text-align:center;color:#64748b;font:14px system-ui">Pick a merchant to see preview</div>'}
              style={{
                width: size === '4x6' ? '4in' : size === '6x3' ? '6in' : '4in',
                height: size === '4x6' ? '6in' : size === '6x3' ? '3in' : '4in',
                background: 'white',
                border: '1px solid #cbd5e1',
                borderRadius: 4,
              }}
            />
          </div>
          <div className="text-[10px] text-slate-400 mt-1 text-center">Shown at actual print size · uses synthetic test order data · refreshes as you change size/template/header/footer</div>
        </div>

        {/* Header + Footer */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block uppercase">Header line (optional)</label>
            <input value={header} onChange={(e) => setHeader(e.target.value)} maxLength={120} placeholder="e.g. www.acme.in · Care: 1800-xxx" className="input w-full" />
            <div className="text-[10px] text-slate-400 mt-0.5">Max 120 chars · printed at top of every label</div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block uppercase">Footer line (optional)</label>
            <input value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="e.g. Thank you for shopping with us!" className="input w-full" />
            <div className="text-[10px] text-slate-400 mt-0.5">Printed at bottom of every label</div>
          </div>
        </div>

        {/* Show merchant logo toggle — on by default; merchant or admin can switch it off.
            Only the MERCHANT's own logo is ever printed — never the platform's. */}
        <div className="p-3 border border-slate-200 rounded-lg">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showLogo}
              onChange={(e) => setShowLogo(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="flex-1">
              <span className="block text-sm font-medium text-slate-900">Print the merchant logo on the address label</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                On by default. Prints <b>this merchant&apos;s own logo</b> at the top of the address label
                (Standard, Invoice &amp; E-com templates). Turn off — or upload no logo — and the label prints
                blank there. The courier platform&apos;s logo is never printed on a merchant&apos;s label.
              </span>
            </span>
          </label>

          {/* Upload lives here as well as on the Profile tab — this is where people look for it. Saves immediately. */}
          <div className="mt-3 flex items-center gap-3 pl-7">
            <div className="w-24 h-14 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              <SignedImage
                src={data?.logoUrl ?? undefined}
                alt="Merchant logo"
                style={{ maxHeight: 52, maxWidth: 92, objectFit: 'contain' }}
                fallback={<span className="text-[10px] text-slate-400">No logo</span>}
              />
            </div>
            <div className="flex-1">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) pickLogo(f); e.target.value = ''; }} />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={logoBusy}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
                  {logoBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {data?.logoUrl ? 'Replace logo' : 'Upload logo'}
                </button>
                {data?.logoUrl && (
                  <button type="button" onClick={() => saveLogo(null)} disabled={logoBusy}
                    className="px-3 py-1.5 text-xs font-medium border border-slate-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5">
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                PNG/JPG · max 500 KB · saved instantly, then shown in the live preview above.
                {!data?.logoUrl && <span className="text-amber-700"> No logo uploaded yet — the label prints without one.</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Show top company banner toggle */}
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition">
          <input
            type="checkbox"
            checked={showTopBanner}
            onChange={(e) => setShowTopBanner(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary"
          />
          <span>
            <span className="block text-sm font-medium text-slate-900">Show top banner (company name &amp; address)</span>
            <span className="block text-xs text-slate-500 mt-0.5">On by default. The blue header band with the company name/address at the top of the label (Standard &amp; Invoice templates). Turn off to hide it.</span>
          </span>
        </label>

        {/* Show From / return-address block toggle */}
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition">
          <input
            type="checkbox"
            checked={showFrom}
            onChange={(e) => setShowFrom(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary"
          />
          <span>
            <span className="block text-sm font-medium text-slate-900">Show From / return-address block</span>
            <span className="block text-xs text-slate-500 mt-0.5">On by default. The sender / "return to" block on the label. Turn off to hide the whole From section.</span>
          </span>
        </label>

        {/* Show product name toggle */}
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition">
          <input
            type="checkbox"
            checked={showProduct}
            onChange={(e) => setShowProduct(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary"
          />
          <span>
            <span className="block text-sm font-medium text-slate-900">Print product name on the address label</span>
            <span className="block text-xs text-slate-500 mt-0.5">Off by default. When on, the order's product name(s) are printed on the label (Standard template). Invoice &amp; E-com templates already list items.</span>
          </span>
        </label>

        {/* Show pickup/From (warehouse) mobile toggle */}
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition">
          <input
            type="checkbox"
            checked={showFromMobile}
            onChange={(e) => setShowFromMobile(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary"
          />
          <span>
            <span className="block text-sm font-medium text-slate-900">Show pickup / From (warehouse) mobile on the label</span>
            <span className="block text-xs text-slate-500 mt-0.5">Off by default — the warehouse/From mobile number is hidden on the address label. Turn on to print it in the From / return-address block.</span>
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
